<template>
  <div class="box-help-page">
    <section class="content-panel">
      <div class="page-head">
        <h1>装箱助手</h1>
        <el-button plain @click="openPackingHistory">装箱历史</el-button>
      </div>

      <div class="flow-note">
        <span v-for="item in flowSteps" :key="item">{{ item }}</span>
      </div>

      <div class="packing-workspace">
        <section class="pack-card">
          <div class="section-head flat">
            <h2>扫码装箱</h2>
          </div>

          <div class="pack-form">
            <div class="field">
              <span>箱子编号</span>
              <div class="box-input">
                <el-input-number
                  ref="boxNoInput"
                  v-model="scanForm.boxNo"
                  :controls="false"
                  :step-strictly="true"
                  placeholder="例如 1"
                  @change="handleBoxNoChange"
                  @keyup.enter.native="handleBoxNoConfirm"
                  :disabled="boxDisabled"
                />
                <el-button type="warning" @click="changeBox">换箱</el-button>
              </div>
              <small>{{ formError.boxNo }}</small>
            </div>

            <div class="field">
              <span>商品条码</span>
              <div class="scan-input">
                <el-input
                  ref="barcodeInput"
                  v-model.trim="scanForm.barcode"
                  clearable
                  autocomplete="off"
                  placeholder="扫描或输入商品条码后按 Enter"
                  @keyup.enter.native="handleBarcodeScan"
                />
                <el-button type="primary" :loading="stockLoading" @click="handleBarcodeScan">查询</el-button>
              </div>
            </div>

            <div class="field">
              <span>商品 SKU</span>
              <el-input :value="selectedBatch ? selectedBatch.sku : ''" readonly placeholder="扫入 SKU 后自动显示" />
            </div>

            <div class="qty-status" v-if="selectedBatch">
              <span class="warehouse-status">仓库：<strong>{{ selectedBatch.warehouse }}</strong></span>
              <span>总库存：<strong>{{ selectedBatch.qty }}</strong></span>
              <span v-if="selectedPackedQty > 0">已装数量：<strong>{{ selectedPackedQty }}</strong></span>
              <span>剩余可装数量：<strong>{{ selectedRemainingQty }}</strong></span>
            </div>
            <div class="scan-empty-tip" v-else>
              扫描商品后展示仓库、总库存和剩余可装数量
            </div>

            <div class="field">
              <span>本次装箱数量</span>
              <el-input-number
                v-model="scanForm.qty"
                :min="1"
                :controls="false"
                :step-strictly="true"
                placeholder="请输入数量"
                @change="validatePackingForm(false)"
              />
              <small>{{ formError.qty }}</small>
            </div>

            <el-button
              class="submit-btn"
              type="success"
              :loading="submitLoading"
              :disabled="!scanForm.boxNo || !selectedBatch || selectedRemainingQty <= 0 || submitLoading"
              @click="submitPacking"
            >
              提交装箱
            </el-button>
          </div>
        </section>

        <section class="records-section">
          <div class="section-head">
            <div class="title-with-stats">
              <h2>已装箱数据</h2>
              <span>箱数：<strong>{{ boxCount }}</strong></span>
              <span>总数量：<strong>{{ packedTotalQty }}</strong></span>
            </div>
            <el-button
              type="success"
              :loading="completeLoading"
              :disabled="!packedRecords.length || completeLoading"
              @click="finishPacking"
            >
              装箱完成
            </el-button>
          </div>

          <div class="table-wrap" v-loading="currentLoading">
            <table class="records-table">
              <colgroup>
                <col class="records-col-boxes" />
                <col class="records-col-sku" />
                <col class="records-col-num" />
                <col class="records-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>所在箱号</th>
                  <th>商品 SKU</th>
                  <th>已装数量</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!recordsBySkuBox.length" class="empty-row">
                  <td colspan="4">
                    <div class="empty-box">
                      <strong>暂无已装箱数据</strong>
                      <span>扫码商品条码并提交装箱后按 SKU 和箱号展示</span>
                    </div>
                  </td>
                </tr>
                <tr v-for="row in recordsBySkuBox" :key="row.key">
                  <td>{{ row.boxNo }}</td>
                  <td><span class="sku-code">{{ row.sku }}</span></td>
                  <td><strong>{{ row.packedQty }}</strong></td>
                  <td>
                    <div class="op-group">
                      <button class="link-btn" type="button" @click="openEditRecord(row.key)">修改</button>
                      <button class="link-btn danger" type="button" @click="deleteRecordGroup(row.key)">删除</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </section>

    <EditRecordDialog ref="editRecordDialog" @confirm="confirmEditQty" />
    <OutboundConfirmDialog ref="outboundConfirmDialog" @confirm="mabangOutbound" />
    <PackingHistoryDrawer
      ref="packingHistoryDrawer"
      :history-list="historyList"
      :loading="historyLoading"
      :pagination="historyPagination"
      @query="queryHistory"
      @download="downloadHistoryPacking"
      @outbound="mabangOutbound"
      @open-log="openOutboundLog"
    />
    <OutboundLogDialog ref="outboundLogDialog" />
  </div>
</template>

<script>
import {
  completePackingAssistant,
  deletePackingAssistantDetail,
  getPackingAssistantCurrent,
  mabangOutPackingAssistant,
  queryPackingAssistantHistory,
  queryPackingAssistantMabangOutLog,
  queryPackingAssistantStock,
  savePackingAssistantDetail,
  updatePackingAssistantDetail,
} from "@/apis/boxHelp";
import EditRecordDialog from "./components/EditRecordDialog.vue";
import OutboundConfirmDialog from "./components/OutboundConfirmDialog.vue";
import OutboundLogDialog from "./components/OutboundLogDialog.vue";
import PackingHistoryDrawer from "./components/PackingHistoryDrawer.vue";
import { audioPay } from "@/utils/tools";

export default {
  name: "BoxHelp",
  components: {
    EditRecordDialog,
    OutboundConfirmDialog,
    OutboundLogDialog,
    PackingHistoryDrawer,
  },
  data() {
    return {
      // 页面顶部流程说明文案。
      flowSteps: [
        "马帮成品上架",
        "使用装箱助手装箱",
        "核对数量完成装箱",
        "下载装箱清单",
        "马帮库存自动出库",
      ],
      // 当前未完成装箱单的装箱单号。
      currentPackCode: "",
      // 当前未完成装箱单的主单乐观锁版本。
      currentVersion: 0,
      // 当前扫码查询到的马帮库存信息。
      currentStock: null,
      // 扫码装箱表单数据。
      scanForm: {
        // 商品条码输入值，可输入批次包条码或 SKU。
        barcode: "",
        // 本次装箱数量。
        qty: undefined,
        // 本次装入的箱子编号。
        boxNo: undefined,
      },
      // 扫码装箱表单错误文案。
      formError: {
        // 本次装箱数量错误文案。
        qty: "",
        // 箱子编号错误文案。
        boxNo: "",
      },
      // 当前轮次已提交的装箱明细记录。
      packedRecords: [],
      // 装箱历史列表。
      historyList: [],
      // 装箱历史分页信息。
      historyPagination: {
        // 当前页码。
        current: 1,
        // 每页条数。
        size: 20,
        // 总记录数。
        total: 0,
      },
      // 最近一次装箱历史查询条件，用于出库后刷新当前筛选。
      lastHistoryQuery: {
        // 历史筛选开始时间。
        startDate: "",
        // 历史筛选结束时间。
        endDate: "",
        // 历史查询页码。
        pageNum: 1,
        // 历史查询每页条数。
        pageSize: 20,
      },
      // 当前未完成装箱单加载状态。
      currentLoading: false,
      // SKU 库存查询加载状态。
      stockLoading: false,
      // 装箱提交加载状态。
      submitLoading: false,
      // 装箱完成加载状态。
      completeLoading: false,
      // 装箱历史加载状态。
      historyLoading: false,
      // 全局扫码枪输入缓存，支持焦点不在条码框时连续扫码。
      scanBuffer: "",
      // 最近一次全局扫码缓存更新时间。
      scanBufferAt: 0,
      boxDisabled: false
    };
  },
  computed: {
    // 当前扫描识别到的 SKU 库存记录。
    selectedBatch: function () {
      return this.currentStock;
    },
    // 当前 SKU 在当前装箱单中的已装箱数量。
    selectedPackedQty: function () {
      return this.selectedBatch ? Number(this.selectedBatch.packedQty || 0) : 0;
    },
    // 当前 SKU 剩余可装箱数量。
    selectedRemainingQty: function () {
      return this.selectedBatch ? Number(this.selectedBatch.remainingQty || 0) : 0;
    },
    // 当前已装箱数据中去重后的箱号数量。
    boxCount: function () {
      return new Set(this.packedRecords.map((item) => item.boxNo)).size;
    },
    // 当前已装箱数据的总数量。
    packedTotalQty: function () {
      return this.recordsBySkuBox.reduce((sum, item) => sum + item.packedQty, 0);
    },
    // 按 SKU + 箱号聚合后的已装箱列表数据。
    recordsBySkuBox: function () {
      // 聚合容器，key 为 SKU + 箱号。
      const map = {};
      this.packedRecords.forEach((record, rawIndex) => {
        // 当前记录所属的聚合 key。
        const key = this.recordGroupKey(record);
        if (!map[key]) {
          map[key] = {
            key,
            detailId: record.detailId,
            version: record.version,
            sku: record.sku,
            boxNo: record.boxNo,
            packedQty: 0,
            items: [],
          };
        }
        if (record.totalPackQty !== null) {
          map[key].packedQty = Math.max(map[key].packedQty, record.totalPackQty);
        } else {
          map[key].packedQty += record.qty;
        }
        map[key].items.push({
          ...record,
          rawIndex,
        });
      });
      return Object.keys(map)
        .map((key) => map[key])
        
    },
  },
  created() {
    // 页面初始化时拉取当前未完成装箱单。
    this.loadCurrentPacking();
  },
  mounted() {
    // 监听全局键盘输入，兼容扫码枪直接输入。
    document.addEventListener("keydown", this.handleGlobalScannerKeydown);
    this.focusBoxNoInput();
  },
  beforeDestroy() {
    // 页面销毁时移除全局扫码监听。
    document.removeEventListener("keydown", this.handleGlobalScannerKeydown);
  },
  methods: {
    // 加载当前未完成装箱单，并同步页面右侧已装箱明细。
    loadCurrentPacking: async function (showLoading = true) {
      if (showLoading) this.currentLoading = true;
      try {
        // 当前未完成装箱单接口响应。
        const res = await getPackingAssistantCurrent();
        if (res.code !== 0) return;

        // 标准化后的当前装箱单数据。
        const current = this.normalizeCurrentPacking(res.data);
        this.currentPackCode = current.packCode;
        this.currentVersion = current.version;
        this.packedRecords = current.records;
      } finally {
        this.ensureBoxNo();
        if (showLoading) this.currentLoading = false;
      }
    },
    // 将当前未完成装箱单接口数据标准化为页面可用结构。
    normalizeCurrentPacking: function (data) {
      // 当前装箱单原始数据。
      const source = data || {};
      // 当前装箱单明细原始列表。
      const detailList = Array.isArray(source.detailList) ? source.detailList : [];
      return {
        packCode: source.packCode || "",
        version: this.toNumber(source.version || source.packVersion || source.mainVersion || source.orderVersion),
        records: detailList.map((item) => this.normalizeDetail(item)),
      };
    },
    // 将装箱明细接口行标准化为页面列表行。
    normalizeDetail: function (row) {
      // 当前明细原始数据。
      const source = row || {};
      // 当前明细数量。
      const qty = this.toNumber(source.packQty);
      // 当前明细聚合数量。
      const totalPackQty = source.totalPackQty === undefined || source.totalPackQty === null ? null : this.toNumber(source.totalPackQty);
      return {
        id: source.detailId || `${source.sku || ""}_${source.boxNo || ""}_${source.version || ""}`,
        detailId: source.detailId,
        version: this.toNumber(source.version),
        packCode: source.packCode || "",
        sku: source.sku || "",
        qty,
        totalPackQty,
        boxNo: source.boxNo || "",
        mabangStockQty: this.toNumber(source.mabangStockQty),
        mabangOutQty: this.toNumber(source.mabangOutQty),
        warehouse: source.mabangWarehouseName || "",
        location: source.warehouseLocation || "",
        raw: source,
      };
    },
    // 处理条码输入框回车扫码。
    handleBarcodeScan: function () {
      if (!this.validateBoxNo(true)) return;
      this.selectBatch(this.scanForm.barcode, true);
    },
    // 根据扫码值查询 SKU 库存，并自动带出数量与默认箱号。
    selectBatch: async function (value, showSuccess) {
      // 清洗后的扫码值。
      const cleanValue = String(value || "").trim();
      if (!cleanValue) {
        this.currentStock = null;
        this.scanForm.qty = undefined;
        this.formError.qty = "";
        this.$message.warning("请扫描或输入商品条码");
        return;
      }

      this.stockLoading = true;
      try {
        // SKU 库存查询接口响应。
        const res = await queryPackingAssistantStock({ sku: cleanValue })
        if (!res.success) {
          this.currentStock = null;
          this.scanForm.qty = undefined;
          this.formError.qty = "";
          audioPay('SKU不存在')
          return;
        }

        // 标准化后的 SKU 库存信息。
        const stock = this.normalizeStock(res.data);
        // 当前 SKU 剩余可装数量。
        const remaining = this.toNumber(stock.remainingQty);
        this.currentStock = stock;
        this.scanForm.barcode = cleanValue;
        this.scanForm.qty = remaining > 0 ? remaining : undefined;
        this.scanForm.boxNo = this.scanForm.boxNo || undefined
        this.formError.qty = "";
        this.formError.boxNo = "";

        if (remaining <= 0) {
          this.$message.warning("该商品已全部装箱");
        } else if (showSuccess) {
          this.boxDisabled = true
          this.$message.success(`已识别 ${stock.sku}`);
        }
      } finally {
        this.stockLoading = false;
      }
    },
    // 将库存接口数据标准化为页面展示结构。
    normalizeStock: function (row) {
      // 当前库存原始数据。
      const source = row || {};
      return {
        sku: source.sku || "",
        warehouse: source.mabangWarehouseName || source.warehouseName || "-",
        qty: this.toNumber(source.mabangStockQty),
        packedQty: this.toNumber(source.packedQty),
        remainingQty: this.toNumber(source.remainingQty),
        loginWarehouseName: source.warehouseName || "",
        raw: source,
      };
    },
    // 获取扫码后默认填充的箱号。
    nextDefaultBoxNo: function () {
      // 已装箱列表中的数字箱号集合。
      const boxNoList = this.packedRecords
        .map((item) => Number(item.boxNo))
        .filter((item) => Number.isInteger(item) && item > 0);
      if (!boxNoList.length) return 1;

      // 当前最大箱号。
      const maxBoxNo = Math.max(...boxNoList);
      return maxBoxNo || 1;
    },
    // 确保当前工作箱号有默认值。
    ensureBoxNo: function () {
      // if (this.scanForm.boxNo) return;
      this.scanForm.boxNo = this.scanForm.boxNo || undefined
    },
    // 校验当前工作箱号是否可用于后续扫码。
    validateBoxNo: function (showMessage) {
      // 原始箱号输入值。
      const boxValue = this.scanForm.boxNo;
      // 标准化后的箱号。
      const boxNo = Number(boxValue);
      // 箱号错误文案。
      let boxError = "";

      if (boxValue === undefined || boxValue === null || boxValue === "") {
        boxError = "请先填写箱子编号";
      } else if (!Number.isInteger(boxNo)) {
        boxError = "箱子编号必须为整数";
      } else if (boxNo <= 0) {
        boxError = "箱子编号必须大于 0";
      }

      this.formError.boxNo = boxError;
      if (boxError && showMessage) {
        this.$message.warning(boxError);
        this.focusBoxNoInput();
      }
      return !boxError;
    },
    // 箱号输入变化时只校验箱号本身。
    handleBoxNoChange: function () {
      this.validateBoxNo(false);
    },
    // 箱号输入完成后聚焦商品条码，进入扫码流程。
    handleBoxNoConfirm: function () {
      if (this.validateBoxNo(true)) this.focusBarcodeInput();
    },
    // 切换当前工作箱号，并清空本次 SKU 扫描状态。
    changeBox: function () {
      this.scanForm.boxNo = '';
      this.scanForm.barcode = "";
      this.currentStock = null;
      this.scanForm.qty = undefined;
      this.formError.qty = "";
      this.formError.boxNo = undefined;
      this.boxDisabled = false
      // this.$message.success(`已切换到箱子 ${nextBoxNo}`);
      this.focusBarcodeInput();
    },
    // 校验扫码装箱表单，返回可提交状态和标准化数量。
    validatePackingForm: function (showErrors) {
      // 当前识别到的 SKU 库存。
      const batch = this.selectedBatch;
      // 原始装箱数量输入值。
      const qtyValue = this.scanForm.qty;
      // 原始箱号输入值。
      const boxValue = this.scanForm.boxNo;
      // 标准化后的装箱数量。
      const qty = Number(qtyValue);
      // 标准化后的箱号。
      const boxNo = Number(boxValue);
      // 当前 SKU 剩余可装数量。
      const remaining = batch ? this.toNumber(batch.remainingQty) : 0;
      // 装箱数量错误文案。
      let qtyError = "";
      // 箱号错误文案。
      let boxError = "";
      // 表单级错误文案。
      let message = "";

      if (!batch) {
        message = "请先扫描商品条码";
      } else if (remaining <= 0) {
        message = "该商品已全部装箱";
      }

      if (qtyValue === undefined || qtyValue === null || qtyValue === "") {
        qtyError = "请输入本次装箱数量";
      } else if (!Number.isInteger(qty)) {
        qtyError = "本次装箱数量必须为整数";
      } else if (qty <= 0) {
        qtyError = "本次装箱数量必须大于 0";
      } else if (batch && qty > remaining) {
        qtyError = `本次装箱数量不能超过剩余数量 ${remaining}`;
      }

      if (boxValue === undefined || boxValue === null || boxValue === "") {
        boxError = "请输入箱子编号";
      } else if (!Number.isInteger(boxNo)) {
        boxError = "箱子编号必须为整数";
      } else if (boxNo <= 0) {
        boxError = "箱子编号必须大于 0";
      }

      if (showErrors || qtyValue || boxValue) {
        this.formError.qty = qtyError;
        this.formError.boxNo = boxError;
      }

      return {
        ok: Boolean(batch) && remaining > 0 && !qtyError && !boxError,
        batch,
        qty,
        boxNo,
        message: message || qtyError || boxError,
      };
    },
    // 提交本次装箱数据到后端装箱明细。
    submitPacking: async function () {
      // 表单校验结果。
      const validation = this.validatePackingForm(true);
      if (!validation.ok) {
        this.$message.error(validation.message);
        return;
      }

      // 本次提交装箱明细请求参数。
      const params = {
        boxNo: String(validation.boxNo),
        packCode: this.currentPackCode || undefined,
        packQty: validation.qty,
        sku: validation.batch.sku,
      };

      this.submitLoading = true;
      try {
        // 提交装箱明细接口响应。
        const res = await savePackingAssistantDetail(params);
        if (res.code !== 0) return;

        // 接口返回的装箱明细。
        const detail = res.data || {};
        this.currentPackCode = detail.packCode || this.currentPackCode;
        await this.loadCurrentPacking(false);
        this.clearScanForm(true);
        this.boxDisabled = false
        this.$message.success("已提交装箱");
      } finally {
        this.submitLoading = false;
      }
    },
    // 清空扫码表单，并按当前流程决定是否保留箱号。
    clearScanForm: function (keepBoxNo = true) {
      // 清空前的当前工作箱号。
      const currentBoxNo = this.scanForm.boxNo;
      this.scanForm.barcode = "";
      this.currentStock = null;
      this.scanForm.qty = undefined;
      this.scanForm.boxNo = keepBoxNo ? currentBoxNo : undefined;
      this.formError.qty = "";
      this.formError.boxNo = "";
      if (keepBoxNo) {
        this.focusBarcodeInput();
      } else {
        this.focusBoxNoInput();
      }
    },
    // 聚焦条码输入框，方便连续扫码。
    focusBarcodeInput: function () {
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
      });
    },
    // 聚焦箱号输入框，确保先填箱号再扫 SKU。
    focusBoxNoInput: function () {
      this.$nextTick(() => {
        if (this.$refs.boxNoInput) this.$refs.boxNoInput.focus();
      });
    },
    // 生成已装箱记录的聚合 key。
    recordGroupKey: function (record) {
      return `${record.sku}__${record.boxNo}`;
    },
    // 删除指定 SKU + 箱号维度的后端装箱明细。
    deleteRecordGroup: async function (key) {
      // 待删除的聚合记录。
      const group = this.recordsBySkuBox.find((item) => item.key === key);
      if (!group) return;
      if (!group.detailId) {
        this.$message.error("缺少明细ID，无法删除");
        return;
      }

      // 被删除记录的 SKU，用于刷新当前库存信息。
      const sku = group.sku;
      // 当前聚合记录下可删除的明细列表。
      const detailList = group.items.filter((item) => item.detailId);

      // 当前待删除明细。
      for (const record of detailList) {
        // 删除装箱明细请求参数。
        const params = {
          detailId: record.detailId,
          version: this.toNumber(record.version),
        };
        // 删除装箱明细接口响应。
        const res = await deletePackingAssistantDetail(params);
        if (res.code !== 0) return;
      }

      await this.loadCurrentPacking(false);
      // await this.refreshSelectedStock(sku);
      this.$message.success(`已删除 ${group.sku} / 箱号 ${group.boxNo}`);
    },
    // 打开修改数量弹窗。
    openEditRecord: function (key) {
      // 待修改的聚合记录。
      const group = this.recordsBySkuBox.find((item) => item.key === key);
      if (!group) {
        this.$message.error("未找到该装箱记录");
        return;
      }
      // 当前聚合记录最大可修改数量。
      const maxQty = this.maxEditableQtyForGroup(group);
      this.$refs.editRecordDialog.open({
        key,
        qty: group.packedQty,
        maxQty,
        meta: `${group.sku} / 箱号 ${group.boxNo} / 最大可改 ${maxQty}`,
      });
    },
    // 计算某个 SKU + 箱号聚合记录的最大可修改数量。
    maxEditableQtyForGroup: function (group) {
      // 当前 SKU 的马帮库存快照。
      const skuStockQty = group.items.reduce((max, item) => Math.max(max, this.toNumber(item.mabangStockQty)), 0);
      if (!skuStockQty) return group.packedQty;

      // 当前 SKU 在其他箱号中已占用的数量。
      const usedByOthers = this.recordsBySkuBox
        .filter((item) => item.sku === group.sku && item.key !== group.key)
        .reduce((total, item) => total + this.toNumber(item.packedQty), 0);
      return Math.max(group.packedQty, skuStockQty - usedByOthers);
    },
    // 接收修改弹窗确认事件，并调用接口修改已装箱数量。
    confirmEditQty: async function ({ key, qty, done, fail }) {
      // 待修改的聚合记录。
      const group = this.recordsBySkuBox.find((item) => item.key === key);
      // 修改后的数量。
      const nextQty = Number(qty);
      if (!group) {
        done();
        return;
      }
      if (!group.detailId) {
        fail("缺少明细ID，无法修改");
        return;
      }

      // 修改装箱明细请求参数。
      const params = {
        detailId: group.detailId,
        packQty: nextQty,
        version: this.toNumber(group.version),
      };
      try {
        // 修改装箱明细接口响应。
        const res = await updatePackingAssistantDetail(params);
        if (res.code !== 0) return;

        await this.loadCurrentPacking(false);
        // await this.refreshSelectedStock(group.sku);
        done();
        this.$message.success("已修改数量");
      } catch (error) {
        fail("修改失败，请重试");
      }
    },
    // 完成当前轮装箱，调用后端完成接口并询问是否马帮出库。
    finishPacking: async function () {
      if (!this.packedRecords.length) {
        this.$message.warning("暂无装箱数据，不能完成");
        return;
      }
      if (!this.currentPackCode) {
        this.$message.error("未获取装箱单号，请刷新后重试");
        return;
      }

      // 固化本轮装箱记录，避免后续清空影响历史。
      const records = this.packedRecords.map((item) => ({ ...item }));
      // 当前待完成的装箱单号。
      const packCode = this.currentPackCode;
      // 装箱完成请求参数。
      const params = {
        needMabangOut: false,
        packCode,
        version: this.toNumber(this.currentVersion),
      };

      this.completeLoading = true;
      try {
        // 装箱完成接口响应。
        const res = await completePackingAssistant(params);
        if (res.code !== 0) return;

        // 完成后返回的历史记录。
        const history = this.normalizeHistoryRow(res.data || { packCode });
        // 装箱清单文件名。
        const filename = history.packingListName || `${history.packingNo || packCode}_装箱清单.xls`;
        this.downloadExcel(records, filename);
        this.mergeHistoryRow(history);
        this.clearCurrentPacking();
        this.clearScanForm(false);
        await this.loadCurrentPacking(false);
        this.$refs.outboundConfirmDialog.open({
          packingNo: history.packingNo || packCode,
          version: history.version,
        });
      } finally {
        this.completeLoading = false;
      }
    },
    // 清空当前装箱单状态。
    clearCurrentPacking: function () {
      this.currentPackCode = "";
      this.currentVersion = 0;
      this.packedRecords = [];
    },
    // 执行马帮出库，并同步刷新历史记录。
    mabangOutbound: async function (payload) {
      // 待出库装箱单号。
      const packCode = typeof payload === "object" ? payload.packingNo || payload.packCode : payload;
      if (!packCode) {
        this.$message.error("缺少装箱单号，无法出库");
        return;
      }

      // 待出库历史行。
      const item = typeof payload === "object" ? payload : this.findHistory(packCode) || {};
      if (item.outboundStatus === "已出库") {
        this.$message.warning(`${packCode} 已完成马帮出库`);
        return;
      }

      // 马帮出库请求参数。
      const params = {
        packCode,
        version: this.toNumber(item.version || this.currentVersion),
      };
      // 马帮出库接口响应。
      const res = await mabangOutPackingAssistant(params);
      if (res.code !== 0) return;

      // 马帮出库后返回的历史记录。
      const history = this.normalizeHistoryRow(res.data || { packCode });
      this.mergeHistoryRow(history);
      await this.queryHistory(this.lastHistoryQuery);
      this.$message.success(`已触发 ${packCode} 马帮出库`);
    },
    // 打开马帮出库履历弹窗，并按装箱单号查询后端履历。
    openOutboundLog: async function (row) {
      // 当前历史行装箱单号。
      const packCode = row.packCode || row.packingNo;
      if (!packCode) {
        this.$message.error("缺少装箱单号，无法查看履历");
        return;
      }

      // 马帮出库履历接口响应。
      const res = await queryPackingAssistantMabangOutLog({ packCode });
      if (res.code !== 0) return;

      // 履历分页原始数据。
      const pageData = res.data || {};
      // 履历接口原始列表。
      const records = Array.isArray(pageData.records) ? pageData.records : [];
      // 标准化后的履历列表。
      const logs = records.map((item) => this.normalizeOutboundLog(item));
      this.$refs.outboundLogDialog.open(logs);
    },
    // 将马帮出库履历接口行标准化为弹窗展示结构。
    normalizeOutboundLog: function (row) {
      // 当前履历原始数据。
      const source = row || {};
      return {
        ...row,
        sku: source.sku || "",
        qty: this.toNumber(source.successQty === undefined ? source.requestQty : source.successQty),
        outboundTime: this.formatApiDateTime(source.outTime),
        remainingQty: source.remainingQty || source.stockQty || "-",
        location: source.warehouseLocation || source.mabangWarehouseName || "-",
        errorMsg: source.errorMsg || "",
        raw: source,
      };
    },
    // 下载历史装箱单的装箱清单。
    downloadHistoryPacking: function (row) {
      // 历史记录中的后端装箱清单地址。
      const url = row.packingListUrl;
      if (url) {
        window.open(url, "_blank");
        return;
      }

      // 历史记录中的本地明细兜底数据。
      const records = row.records || [];
      if (records.length) {
        this.downloadExcel(records, row.packingListName || `${row.packingNo}_装箱清单.xls`);
        return;
      }

      this.$message.warning("当前历史记录暂无可下载的装箱清单");
    },
    // 根据装箱单号查找历史记录。
    findHistory: function (packingNo) {
      return this.historyList.find((item) => item.packingNo === packingNo);
    },
    // 打开装箱历史抽屉，真实查询由抽屉 open 事件触发。
    openPackingHistory: function () {
      this.$refs.packingHistoryDrawer.open();
    },
    // 查询装箱历史分页列表。
    queryHistory: async function (payload = {}) {
      // 历史查询页码。
      const pageNum = Number(payload.pageNum || this.historyPagination.current || 1);
      // 历史查询每页条数。
      const pageSize = Number(payload.pageSize || this.historyPagination.size || 20);
      // 兼容旧入口传入的历史查询日期。
      const date = payload.date || "";
      // 历史查询开始时间。
      const startDate = payload.startDate || (date ? `${date} 00:00:00` : "");
      // 历史查询结束时间。
      const endDate = payload.endDate || (date ? `${date} 23:59:59` : "");
      // 历史查询请求参数。
      const params = {
        pageNum,
        pageSize,
      };

      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      this.lastHistoryQuery = {
        startDate,
        endDate,
        pageNum,
        pageSize,
      };
      this.historyLoading = true;
      try {
        // 装箱历史接口响应。
        const res = await queryPackingAssistantHistory(params);
        if (res.code !== 0) return;

        // 历史分页原始数据。
        const pageData = res.data || {};
        // 历史接口原始列表。
        const records = Array.isArray(pageData.records) ? pageData.records : [];
        this.historyList = records.map((item) => this.normalizeHistoryRow(item));
        this.historyPagination = {
          current: Number(pageData.current || pageNum),
          size: Number(pageData.size || pageSize),
          total: Number(pageData.total || 0),
        };
      } finally {
        this.historyLoading = false;
      }
    },
    // 将装箱历史接口行标准化为抽屉展示结构。
    normalizeHistoryRow: function (row) {
      // 当前历史原始数据。
      const source = row || {};
      // 装箱单号。
      const packingNo = source.packCode || source.packingNo || "";
      // 装箱完成时间。
      const completedAt = this.formatApiDateTime(source.finishTime);
      return {
        packingNo,
        packCode: packingNo,
        version: this.toNumber(source.version || source.packVersion || source.mainVersion || source.orderVersion),
        date: this.formatApiDate(source.finishTime),
        completedAt,
        skuCount: this.toNumber(source.skuTypeCount),
        totalQty: this.toNumber(source.totalQty),
        outboundQty: this.toNumber(source.mabangOutQty),
        outboundStatus: this.normalizeOutboundStatus(source),
        packingListName: source.packingListName || `${packingNo}_装箱清单.xls`,
        packingListUrl: source.packingListUrl || "",
        outFailQty: this.toNumber(source.outFailQty),
        outResultMsg: source.outResultMsg || "",
        records: [],
        raw: source,
      };
    },
    // 根据后端状态和出库数量推导页面展示的出库状态。
    normalizeOutboundStatus: function (row) {
      // 当前历史原始数据。
      const source = row || {};
      // 后端状态码。
      const status = Number(source.status);
      // 失败出库数量。
      const failQty = this.toNumber(source.outFailQty);
      // 已出库数量。
      const outQty = this.toNumber(source.mabangOutQty);
      // 装箱总数量。
      const totalQty = this.toNumber(source.totalQty);
      if (failQty > 0 || status === 3 || status === 4) return "出库失败";
      if (status === 2 || (totalQty > 0 && outQty >= totalQty)) return "已出库";
      return "待出库";
    },
    // 将最新历史记录合并到当前历史列表中。
    mergeHistoryRow: function (history) {
      if (!history || !history.packingNo) return;
      // 已存在的历史行索引。
      const index = this.historyList.findIndex((item) => item.packingNo === history.packingNo);
      if (index >= 0) {
        this.$set(this.historyList, index, {
          ...this.historyList[index],
          ...history,
        });
        return;
      }
      this.historyList.unshift(history);
    },
    // 刷新当前选中 SKU 的库存信息。
    refreshSelectedStock: async function (sku) {
      if (!this.currentStock || this.currentStock.sku !== sku) return;
      await this.selectBatch(sku, false);
    },
    // 将装箱记录按箱号 + SKU 聚合为导出行。
    groupExportRows: function (records) {
      // 导出行聚合容器。
      const map = {};
      records.forEach((record) => {
        // 当前导出行聚合 key。
        const key = `${record.boxNo}__${record.sku}`;
        if (!map[key]) {
          map[key] = {
            boxNo: record.boxNo,
            sku: record.sku,
            qty: 0,
          };
        }
        if (record.totalPackQty !== null && record.totalPackQty !== undefined) {
          map[key].qty = Math.max(map[key].qty, record.totalPackQty);
        } else {
          map[key].qty += record.qty;
        }
      });
      return Object.keys(map)
        .map((key) => map[key])
        .sort((a, b) => {
          // 箱号排序结果。
          const boxNoCompare = Number(a.boxNo) - Number(b.boxNo);
          if (boxNoCompare) return boxNoCompare;
          return a.sku.localeCompare(b.sku);
        });
    },
    // 导出 Excel 兼容 xls 装箱清单。
    downloadExcel: function (records, filename) {
      if (!records || !records.length) {
        this.$message.warning("暂无装箱清单可下载");
        return;
      }
      // 已按箱号 + SKU 聚合后的导出行。
      const rows = this.groupExportRows(records);
      // Excel 可识别的 HTML 表格内容。
      const html = `
        <html>
          <head><meta charset="UTF-8" /></head>
          <body>
            <table border="1">
              <thead>
                <tr>
                  <th width="200">箱号</th>
                  <th width="200">SKU</th>
                  <th width="200">件数</th>
                </tr>
              </thead>
              <tbody>
                ${rows.map((item) => `
                  <tr>
                    <td style="text-align:center;">${this.escapeHtml(item.boxNo)}</td>
                    <td>${this.escapeHtml(item.sku)}</td>
                    <td style="text-align:center;">${this.escapeHtml(item.qty)}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </body>
        </html>
      `;
      // xls 文件 Blob。
      const blob = new Blob([`\uFEFF${html}`], { type: "application/vnd.ms-excel;charset=utf-8" });
      // 触发浏览器下载的临时链接。
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 0);
    },
    // 判断键盘事件是否是扫码提交按键。
    isScanCommitKey: function (event) {
      return event.key === "Enter" || event.key === "NumpadEnter" || event.key === "Tab" || event.keyCode === 13 || event.keyCode === 9;
    },
    // 处理全局扫码枪输入，焦点不在输入框时也能识别扫码。
    handleGlobalScannerKeydown: function (event) {
      // 当前页面焦点元素。
      const active = document.activeElement;
      // 是否正在普通表单控件里输入。
      const isTypingField = active && ["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName);
      if (isTypingField || event.metaKey || event.ctrlKey || event.altKey) return;

      // 当前键盘事件时间戳。
      const now = Date.now();
      if (now - this.scanBufferAt > 500) this.scanBuffer = "";
      this.scanBufferAt = now;

      if (this.isScanCommitKey(event)) {
        if (!this.scanBuffer) return;
        event.preventDefault();
        this.scanForm.barcode = this.scanBuffer;
        this.handleBarcodeScan();
        this.scanBuffer = "";
        return;
      }

      if (event.key && event.key.length === 1) {
        this.scanBuffer += event.key;
      }
    },
    // 将数字补齐到两位。
    pad: function (value) {
      return String(value).padStart(2, "0");
    },
    // 格式化日期为 yyyy-MM-dd。
    formatDate: function (date) {
      return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
    },
    // 格式化日期时间为 yyyy-MM-dd HH:mm:ss。
    formatDateTime: function (date) {
      return `${this.formatDate(date)} ${this.pad(date.getHours())}:${this.pad(date.getMinutes())}:${this.pad(date.getSeconds())}`;
    },
    // 将接口日期转成 yyyy-MM-dd。
    formatApiDate: function (value) {
      if (!value) return "";
      // 接口日期对象。
      const date = this.parseApiDate(value);
      if (Number.isNaN(date.getTime())) return "";
      return this.formatDate(date);
    },
    // 将接口日期转成 yyyy-MM-dd HH:mm:ss。
    formatApiDateTime: function (value) {
      if (!value) return "";
      // 接口日期对象。
      const date = this.parseApiDate(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return this.formatDateTime(date);
    },
    // 兼容接口返回的 ISO 或 yyyy-MM-dd HH:mm:ss 日期格式。
    parseApiDate: function (value) {
      // 原始日期解析结果。
      const rawDate = new Date(value);
      if (!Number.isNaN(rawDate.getTime())) return rawDate;

      // 兼容 Safari 的日期解析结果。
      const compatibleDate = new Date(String(value).replace(/-/g, "/"));
      return compatibleDate;
    },
    // 将接口数值统一转为数字。
    toNumber: function (value) {
      // 标准化后的数字。
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : 0;
    },
    // 转义导出 HTML 中的特殊字符。
    escapeHtml: function (value) {
      return String(value === undefined || value === null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
  },
};
</script>

<style scoped>
.box-help-page {
  --panel: #ffffff;
  --line: #dfe3ea;
  --line-soft: #e8ebf0;
  --text: #333333;
  --muted: #8c939d;
  --blue: #409eff;
  --blue-hover: #2f8fe8;
  --green-bg: #eef8e9;
  --green: #55bf32;
  --red: #ff0000;
  --page-bg: #f4f6f8;
  padding: 0 10px 10px;
  color: var(--text);
  text-align: left;
}

.content-panel {
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel);
  box-shadow: 0 2px 8px rgba(18, 30, 45, 0.06);
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--line);
}

.page-head h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.flow-note {
  min-height: 46px;
  margin: 12px 0 0;
  padding: 0 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  border: 1px solid #cfe5f8;
  border-radius: 3px;
  background: #dceefe;
  color: #333333;
  line-height: 1.4;
  font-weight: 600;
}

.flow-note span {
  display: inline-flex;
  align-items: center;
}

.flow-note span + span::before {
  content: ">";
  padding: 0 10px;
  color: var(--blue);
  font-weight: 700;
}

.packing-workspace {
  display: grid;
  grid-template-columns: 390px minmax(0, 1fr);
  gap: 12px;
  margin-top: 12px;
}

.pack-card,
.records-section {
  border: 1px solid var(--line);
  background: #ffffff;
}

.section-head {
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 12px;
  background: #f7f8fa;
  border-bottom: 1px solid var(--line);
}

.section-head.flat {
  justify-content: flex-start;
}

.section-head h2 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}

.pack-form {
  display: grid;
  gap: 12px;
  padding: 14px;
}

.field {
  display: grid;
  gap: 8px;
}

.field span {
  color: #596675;
  font-weight: 500;
}

.box-input,
.scan-input {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 92px;
  gap: 8px;
}

.field ::v-deep .el-input-number,
.field ::v-deep .el-input {
  width: 100%;
}

.field small {
  min-height: 18px;
  color: var(--red);
  font-size: 12px;
}

.qty-status {
  min-height: 24px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  color: var(--red);
  font-weight: 600;
}

.qty-status .warehouse-status {
  color: #596675;
}

.qty-status strong {
  font-size: 15px;
}

.scan-empty-tip {
  min-height: 24px;
  color: var(--muted);
  line-height: 24px;
}

.submit-btn {
  width: 100%;
  margin-top: 2px;
}

.title-with-stats {
  display: flex;
  align-items: center;
  gap: 18px;
}

.title-with-stats span {
  color: #596675;
}

.title-with-stats strong {
  display: inline-block;
  min-width: 18px;
  margin-left: 2px;
  color: var(--red);
  font-size: 20px;
  line-height: 1;
  font-weight: 800;
  vertical-align: -1px;
}

.table-wrap {
  overflow: auto;
}

.records-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.records-table {
  min-width: 620px;
}

.records-col-boxes {
  width: 160px;
}

.records-col-sku {
  width: 240px;
}

.records-col-num {
  width: 110px;
}

.records-col-actions {
  width: 120px;
}

.records-table th,
.records-table td {
  height: 48px;
  padding: 8px 12px;
  border: 1px solid var(--line-soft);
  text-align: left;
  vertical-align: middle;
}

.records-table th {
  height: 46px;
  background: #fafafa;
  color: #333333;
  font-weight: 700;
}

.empty-row td {
  height: 260px;
  text-align: center;
}

.empty-box {
  display: grid;
  gap: 10px;
  justify-items: center;
}

.empty-box strong {
  font-size: 14px;
  font-weight: 500;
  color: #b5bac2;
}

.empty-box span {
  color: var(--muted);
}

.sku-code {
  color: #1688ff;
  overflow-wrap: anywhere;
}

.op-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.link-btn {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--blue);
  cursor: pointer;
}

.link-btn.danger {
  color: var(--red);
}

@media (max-width: 1500px) {
  .packing-workspace {
    grid-template-columns: 390px minmax(0, 1fr);
  }
}
</style>
